import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ChevronRight } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

export default function SendEmailPicker({ onPick, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ops/email-templates`, getAuth());
        const visible = data.filter(t => t.active !== false && t.interviewer_visible);
        visible.sort((a, b) => {
          if (a.key === 'post_discovery_followup') return -1;
          if (b.key === 'post_discovery_followup') return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        setTemplates(visible);
      } catch (e) { toast.error('Failed to load templates'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <OpsModal
      title="Send email"
      subtitle="Choose a template"
      icon={Mail}
      onClose={onClose}
      size="sm"
      zClass="z-[60]"
      testid="send-email-picker"
    >
      <div className="p-4 space-y-2">
        {loading && <p className="text-[13px] text-slate-500 px-1 py-2">Loading templates…</p>}
        {!loading && templates.length === 0 && (
          <p className="text-[13px] text-slate-500 px-1 py-2">No email templates are visible to interviewers.</p>
        )}
        {!loading && templates.map(t => {
          const preview = t.description || t.notes || (t.body || '').slice(0, 100).replace(/\n/g, ' ');
          const isAdvanced = t.key === 'post_discovery_followup';
          return (
            <button
              key={t.id}
              onClick={() => onPick(t)}
              data-testid={`pick-template-${t.key}`}
              className="w-full text-left bg-white border border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-md p-3 transition-colors flex items-start gap-3 group"
            >
              <span className="mt-0.5 w-7 h-7 rounded-md bg-slate-100 grid place-items-center text-slate-500 shrink-0 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Mail size={13} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-[13px] font-medium text-slate-900">{t.name}</p>
                  {isAdvanced && <span className="ops-pill bg-blue-50 text-blue-700 border-blue-100">Advanced</span>}
                  {t.is_system && <span className="ops-pill bg-slate-100 text-slate-600 border-slate-200">System</span>}
                </div>
                {preview && <p className="text-[12px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">{preview}</p>}
              </div>
              <ChevronRight size={14} className="text-slate-400 mt-1 shrink-0 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
            </button>
          );
        })}
      </div>
    </OpsModal>
  );
}
